/**
 * Onglet « Organisations » : clients de l'offre Entreprise.
 * Les membres d'une organisation ont le plan Entreprise et ne partagent leurs scénarios qu'entre eux.
 * Les comptes vérifiés des domaines de l'organisation y sont rattachés automatiquement.
 */
import { escapeHtml } from '../utils/html.js';
import { pill } from './adminUi.js';
import { createDeleteConfirmModal } from './ui/DeleteConfirmModal.js';

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('fr-FR') : '—');
const plural = (n, one, many) => `${n} ${n > 1 ? many : one}`;

/**
 * @param {Object} options
 * @param {Function} options.fetchJson - Requête admin authentifiée qui lève une erreur sur réponse non-ok
 * @param {Function} options.rerender - Redessine l'onglet courant
 */
export function createOrganizationsTab({ fetchJson, rerender }) {
    const state = {
        loaded: false,
        loading: false,
        error: null,
        organizations: [],
        creating: false,
        selectedId: null,
        members: [],
        membersLoading: false,
        notice: null,
        formError: null,
        busy: false,
    };

    const selected = () => state.organizations.find((o) => String(o.id) === String(state.selectedId)) || null;
    const jsonBody = (method, body) => ({ method, body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } });

    async function load() {
        state.loading = true;
        rerender();
        try {
            const data = await fetchJson('/api/admin/organizations');
            state.organizations = data.organizations || [];
            state.error = null;
            state.loaded = true;
            if (state.selectedId && !selected()) state.selectedId = null;
        } catch (error) {
            state.error = error.message || 'Impossible de charger les organisations.';
        } finally {
            state.loading = false;
            rerender();
        }
    }

    async function loadMembers() {
        if (!state.selectedId) return;
        state.membersLoading = true;
        rerender();
        try {
            const data = await fetchJson(`/api/admin/organizations/${state.selectedId}/members`);
            state.members = data.members || [];
        } catch (error) {
            state.formError = error.message;
        } finally {
            state.membersLoading = false;
            rerender();
        }
    }

    /** Exécute une écriture, affiche son erreur dans le formulaire et recharge la liste. */
    async function mutate(action, successNotice) {
        state.busy = true;
        state.formError = null;
        state.notice = null;
        rerender();
        try {
            const result = await action();
            state.notice = typeof successNotice === 'function' ? successNotice(result) : successNotice;
            await load();
            if (state.selectedId) await loadMembers();
            return result;
        } catch (error) {
            state.formError = error.message;
            return null;
        } finally {
            state.busy = false;
            rerender();
        }
    }

    function select(id) {
        state.selectedId = String(state.selectedId) === String(id) ? null : id;
        state.creating = false;
        state.members = [];
        state.formError = null;
        state.notice = null;
        rerender();
        loadMembers();
    }

    function startCreate() {
        state.creating = true;
        state.selectedId = null;
        state.formError = null;
        state.notice = null;
        rerender();
        document.getElementById('org-create-name')?.focus();
    }

    function renderCreateForm() {
        if (!state.creating) return '';
        return `
            <form class="ad-form-card ad-form" data-org-create>
                <div class="ad-form-head">
                    <span class="material-symbols-outlined" aria-hidden="true">domain_add</span>
                    <div class="ad-form-heading">
                        <h3 class="ad-form-title">Nouvelle organisation</h3>
                        <p class="ad-form-sub">Les comptes vérifiés de ces domaines passent en offre Entreprise et rejoignent l’organisation.</p>
                    </div>
                    <button class="rd-icon-btn" type="button" data-org-cancel aria-label="Fermer"><span class="material-symbols-outlined">close</span></button>
                </div>
                ${state.formError ? `<div class="ad-alert ad-alert--danger">${escapeHtml(state.formError)}</div>` : ''}
                <div class="ad-field-row">
                    <div class="ad-field">
                        <label class="ad-label" for="org-create-name">Nom <span class="ad-required">*</span></label>
                        <input id="org-create-name" class="ad-input" name="name" required maxlength="120" placeholder="L’Oréal" />
                    </div>
                    <div class="ad-field">
                        <label class="ad-label" for="org-create-domains">Domaines email</label>
                        <input id="org-create-domains" class="ad-input" name="domains" placeholder="loreal.com, loreal.fr" />
                    </div>
                </div>
                <div class="ad-page-actions">
                    <button class="rd-btn rd-btn-light" type="button" data-org-cancel>Annuler</button>
                    <button class="rd-btn rd-btn-night" type="submit" ${state.busy ? 'disabled' : ''}>Créer</button>
                </div>
            </form>
        `;
    }

    function renderTable() {
        if (state.organizations.length === 0) {
            return `<p class="admin-empty">Aucune organisation. Créez-en une pour ouvrir le partage à une entreprise.</p>`;
        }
        return `
            <div class="admin-table-wrapper-modern">
                <table class="admin-table-modern">
                    <thead><tr><th>Organisation</th><th>Domaines</th><th>Membres</th><th>Créée le</th></tr></thead>
                    <tbody>
                        ${state.organizations
                            .map(
                                (o) => `
                            <tr class="admin-table-row-clickable${String(o.id) === String(state.selectedId) ? ' is-selected' : ''}" data-org-id="${escapeHtml(String(o.id))}" tabindex="0">
                                <td><span class="ad-cell-strong">${escapeHtml(o.name)}</span></td>
                                <td>${o.domains.length ? o.domains.map((d) => pill(d, 'neutral', { dot: false, mono: true })).join(' ') : '<span class="ad-muted">—</span>'}</td>
                                <td>${o.memberCount}</td>
                                <td class="ad-cell-muted ad-cell-date">${formatDate(o.createdAt)}</td>
                            </tr>`
                            )
                            .join('')}
                    </tbody>
                </table>
            </div>
        `;
    }

    function renderDetail() {
        const org = selected();
        if (!org) return '';
        const members = state.membersLoading
            ? '<p class="admin-empty">Chargement des membres…</p>'
            : state.members.length === 0
              ? '<p class="admin-empty">Aucun membre pour l’instant.</p>'
              : `<div class="admin-table-wrapper-modern"><table class="admin-table-modern">
                    <thead><tr><th>Email</th><th>Identifiant</th><th>Dernière connexion</th><th></th></tr></thead>
                    <tbody>${state.members
                        .map(
                            (m) => `<tr>
                        <td><span class="ad-cell-strong">${escapeHtml(m.email)}</span></td>
                        <td>${m.username ? `<code class="admin-user-id">${escapeHtml(m.username)}</code>` : '<span class="ad-muted">—</span>'}</td>
                        <td class="ad-cell-muted ad-cell-date">${formatDate(m.lastLoginAt)}</td>
                        <td><button class="rd-btn rd-btn-light" type="button" data-org-remove-member="${escapeHtml(String(m.id))}" data-email="${escapeHtml(m.email)}">Retirer</button></td>
                    </tr>`
                        )
                        .join('')}</tbody></table></div>`;
        return `
            <section class="ad-form-card ad-form" data-org-detail>
                <div class="ad-form-head">
                    <span class="material-symbols-outlined" aria-hidden="true">domain</span>
                    <div class="ad-form-heading">
                        <h3 class="ad-form-title">${escapeHtml(org.name)}</h3>
                        <p class="ad-form-sub">${plural(org.memberCount, 'membre', 'membres')} · offre Entreprise · partage limité à l’organisation</p>
                    </div>
                    <button class="rd-btn ad-btn-danger" type="button" data-org-delete>Supprimer</button>
                </div>
                ${state.formError ? `<div class="ad-alert ad-alert--danger">${escapeHtml(state.formError)}</div>` : ''}
                ${state.notice ? `<div class="ad-alert">${escapeHtml(state.notice)}</div>` : ''}
                <form class="ad-field-row" data-org-update>
                    <div class="ad-field">
                        <label class="ad-label" for="org-edit-name">Nom</label>
                        <input id="org-edit-name" class="ad-input" name="name" required maxlength="120" value="${escapeHtml(org.name)}" />
                    </div>
                    <div class="ad-field">
                        <label class="ad-label" for="org-edit-domains">Domaines email</label>
                        <input id="org-edit-domains" class="ad-input" name="domains" value="${escapeHtml(org.domains.join(', '))}" placeholder="exemple.com" />
                    </div>
                    <div class="ad-field" style="flex: 0 0 auto; justify-content: flex-end;">
                        <button class="rd-btn rd-btn-light" type="submit" ${state.busy ? 'disabled' : ''}>Enregistrer</button>
                    </div>
                </form>
                <form class="ad-field-row" data-org-add-member>
                    <div class="ad-field">
                        <label class="ad-label" for="org-add-member">Ajouter un compte hors domaine (consultant, prestataire…)</label>
                        <input id="org-add-member" class="ad-input" name="email" type="email" required placeholder="prenom.nom@exemple.com" />
                    </div>
                    <div class="ad-field" style="flex: 0 0 auto; justify-content: flex-end;">
                        <button class="rd-btn rd-btn-night" type="submit" ${state.busy ? 'disabled' : ''}>Ajouter</button>
                    </div>
                </form>
                ${members}
            </section>
        `;
    }

    function render() {
        if (state.loading && !state.loaded) return '<section class="admin-section"><p class="admin-empty">Chargement des organisations…</p></section>';
        if (state.error) return `<section class="admin-section"><div class="admin-error"><p>${escapeHtml(state.error)}</p></div></section>`;
        return `
            <section class="admin-section ad-orgs">
                ${renderCreateForm()}
                ${renderTable()}
                ${renderDetail()}
            </section>
        `;
    }

    function confirm({ title, message, itemName, warningMessage, confirmText, onConfirm }) {
        const modal = createDeleteConfirmModal({ title, message, itemName, warningMessage, confirmText, cancelText: 'Annuler', onConfirm });
        modal.show();
    }

    function bind(content) {
        content.querySelectorAll('[data-org-id]').forEach((row) => {
            row.addEventListener('click', () => select(row.dataset.orgId));
            row.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') select(row.dataset.orgId);
            });
        });
        content.querySelectorAll('[data-org-cancel]').forEach((btn) =>
            btn.addEventListener('click', () => {
                state.creating = false;
                state.formError = null;
                rerender();
            })
        );
        content.querySelector('[data-org-create]')?.addEventListener('submit', async (e) => {
            e.preventDefault();
            const form = new FormData(e.currentTarget);
            const result = await mutate(
                () => fetchJson('/api/admin/organizations', jsonBody('POST', { name: form.get('name'), domains: form.get('domains') })),
                (r) => `Organisation créée · ${plural(r.attached, 'compte rattaché', 'comptes rattachés')}.`
            );
            if (result) {
                state.creating = false;
                state.selectedId = result.organization.id;
                await loadMembers();
            }
        });
        content.querySelector('[data-org-update]')?.addEventListener('submit', (e) => {
            e.preventDefault();
            const form = new FormData(e.currentTarget);
            mutate(
                () =>
                    fetchJson(
                        `/api/admin/organizations/${state.selectedId}`,
                        jsonBody('PATCH', { name: form.get('name'), domains: form.get('domains') })
                    ),
                (r) => `Enregistré${r.attached ? ` · ${plural(r.attached, 'compte rattaché', 'comptes rattachés')}` : ''}.`
            );
        });
        content.querySelector('[data-org-add-member]')?.addEventListener('submit', (e) => {
            e.preventDefault();
            const email = new FormData(e.currentTarget).get('email');
            mutate(
                () => fetchJson(`/api/admin/organizations/${state.selectedId}/members`, jsonBody('POST', { email })),
                `${email} ajouté en offre Entreprise.`
            );
        });
        content.querySelectorAll('[data-org-remove-member]').forEach((btn) =>
            btn.addEventListener('click', () =>
                confirm({
                    title: 'Retirer le membre',
                    message: 'Retirer de l’organisation',
                    itemName: btn.dataset.email,
                    warningMessage: 'Le compte repasse en offre <strong>gratuite</strong> et perd les partages faits ou reçus dans l’organisation.',
                    confirmText: 'Retirer',
                    onConfirm: () =>
                        mutate(
                            () => fetchJson(`/api/admin/organizations/${state.selectedId}/members/${btn.dataset.orgRemoveMember}`, { method: 'DELETE' }),
                            `${btn.dataset.email} retiré.`
                        ),
                })
            )
        );
        content.querySelector('[data-org-delete]')?.addEventListener('click', () => {
            const org = selected();
            if (!org) return;
            confirm({
                title: 'Supprimer l’organisation',
                message: 'Supprimer',
                itemName: org.name,
                warningMessage: `Ses ${plural(org.memberCount, 'membre repasse', 'membres repassent')} en offre <strong>gratuite</strong> et les partages internes sont supprimés.`,
                confirmText: 'Supprimer',
                onConfirm: async () => {
                    await mutate(() => fetchJson(`/api/admin/organizations/${org.id}`, { method: 'DELETE' }), null);
                    state.selectedId = null;
                    state.notice = null;
                    rerender();
                },
            });
        });
    }

    return {
        load,
        render,
        bind,
        startCreate,
        get loaded() {
            return state.loaded || state.loading;
        },
    };
}
